import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';
import { Adapt } from './lib/adapt.js';
import { SceneClock } from './lib/scene-clock.js';
import { vdjBarChroma, VdjPost } from './lib/vdjLook.js';

// Contrat du rig partagé : interdit. La seule lumière est l'aurore elle-même.
const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'borealis',
  family: 'organic',
  danceability: 0.50,
  requiresTier: 0,
  energyLevel: 0.30,
  bpmMin: 60,
  bpmMax: 120,
  movement: 0.35,
  tonality: 0.85,
  enabled: true
};

// BOREALIS : une aurore boréale au-dessus d'un lac gelé, entre des montagnes
// et des sapins enneigés, sous un ciel étoilé. Les rideaux ondulent et
// s'embrasent sur la musique ; des couronnes s'ouvrent au zénith ; le lac
// reflète tout. Ambient, downtempo, musique de film : preset lounge de vdjLook
// (aucun flash, séparation RGB sur la mesure). (L'id `aurora` existe déjà :
// un autre visualiseur, plus ancien, sans paysage.)
//
// L'AURORE, en demi-résolution : chaque pixel lance son rayon (caméra
// légèrement levée) dans une couche de ciel entre deux altitudes et y intègre
// une densité en 16 à 32 pas (10 au tier 0), décalés par un bruit entrelacé
// (aucune bande). Les rideaux sont des NAPPES VERTICALES dont l'empreinte au
// sol suit des courbes qui ondulent lentement ; leurs plis et leurs rayons sont
// un bruit 1D le long de la courbe, net (une texture par cellule), qui
// scintille ; le profil vertical a un bord inférieur NET et vif (frange rose)
// et décroît vers le haut ; la couleur suit l'altitude (vert en bas, violet ou
// rouge en haut). Stockée en sqrt dans une cible RGBA8, recomposée en pleine
// résolution avec les étoiles et le paysage (R1).
//
// LE PAYSAGE, en pleine résolution : trois plans de montagnes (bruit),
// sommets enneigés que l'aurore éclaire faiblement ; une rangée de sapins
// enneigés sur la rive, silhouettes noires dont seul le haut des étages prend
// la lumière (R13), bords anti-crénelés au pixel ; le lac gelé reflète le ciel,
// les montagnes et les arbres en miroir, assombri et un peu flou, avec des
// fissures fines (Voronoï dans le plan du lac, en perspective) ; étoiles de
// tailles et de couleurs variées, Voie lactée discrète.
//
// LA MUSIQUE (adapt.js) : temps → une vague de lumière court le long d'un
// rideau (enveloppe douce, sans clignoter) ; mesure → les rideaux ondulent plus
// fort, puis se reposent ; phrase → palette (verte, violette, rose, rarement
// rouge) en fondu de 3 s ; accent → COURONNE, des rayons qui convergent vers le
// zénith en éventail, 4 s d'embrasement ; calme → rideaux lents et pâles.
// Scène (lib/scene-clock.js, 40 s) : une couronne vers le milieu de chaque
// scène ; le ciel est actif dès la première image.

const TIERS = { 0: { steps: 10, curtains: 2, refl: false }, 1: { steps: 16, curtains: 3, refl: true }, 2: { steps: 32, curtains: 4, refl: true } };
const HORIZON = -0.28;                    // screen units (half height): the lake's shore
const FOV_DEG = 60;
const SCENE_S = 40;
const CORONA_S = 4;
const NO_BPM = 90;
const PALETTES = [
  { low: [0.15, 1.0, 0.45], high: [0.55, 0.2, 0.85], fringe: [1.0, 0.25, 0.5] },      // green
  { low: [0.45, 0.3, 1.0], high: [0.9, 0.25, 0.75], fringe: [0.3, 1.0, 0.6] },        // violet
  { low: [1.0, 0.35, 0.65], high: [0.6, 0.3, 1.0], fringe: [0.2, 1.0, 0.5] },         // pink
  { low: [0.2, 0.95, 0.45], high: [1.0, 0.15, 0.2], fringe: [1.0, 0.3, 0.4] }         // red (rare)
];
const PAL_WEIGHTS = [0.4, 0.25, 0.25, 0.1];
const CURTAINS = [[4.5, 1.0, 0.22, 0.3], [6.5, 1.4, 0.16, 2.1], [9.0, 1.8, 0.12, 4.0], [12.5, 2.2, 0.09, 5.3]];   // z, amplitude, frequency, phase

function urlParam(name) {
  if (typeof location === 'undefined') return null;
  return new URLSearchParams(location.search).get(name);
}
function hashStr(s) { let h = 2166136261; for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619) >>> 0; } return h >>> 0; }

const quadVS = `
  precision highp float;
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy * 2.0, 0.0, 1.0); }
`;
const NOISE = `
  uniform sampler2D uNoise;
  float n1(float x, float row) { return texture2D(uNoise, vec2(x / 256.0, (row + 0.5) / 256.0)).r; }
  float n2(vec2 p) { return texture2D(uNoise, p / 256.0).g; }
  float hash12(vec2 p) { vec3 q = fract(vec3(p.xyx) * 0.1031); q += dot(q, q.yzx + 33.33); return fract((q.x + q.y) * q.z); }
`;
const auroraFS = `
  precision highp float;
  ${NOISE}
  uniform float uT, uSteps, uAspect, uTanH, uPitch, uGain;
  uniform vec4 uC[4];          // z, amplitude, frequency, phase
  uniform vec4 uCI;            // intensities
  uniform vec4 uWave;          // curtain, x, amplitude
  uniform vec2 uCorona;        // amplitude, seed
  uniform vec3 uLow, uHigh, uFringe;
  varying vec2 vUv;
  void main() {
    vec2 s = (vUv * 2.0 - 1.0) * vec2(uAspect, 1.0);
    vec3 r0 = normalize(vec3(s.x * uTanH, s.y * uTanH, 1.0));
    float cp = cos(uPitch), sp = sin(uPitch);
    vec3 rd = vec3(r0.x, r0.y * cp + r0.z * sp, -r0.y * sp + r0.z * cp);
    vec3 col = vec3(0.0);
    if (rd.y > 0.004) {
      float t0 = 1.3 / rd.y, t1 = min(7.0 / rd.y, 70.0);
      if (t0 < 70.0) {
        float dt = (t1 - t0) / uSteps;
        float j = fract(52.9829189 * fract(dot(gl_FragCoord.xy, vec2(0.06711056, 0.00583715))));   // interleaved gradient noise
        for (int i = 0; i < 32; i++) {
          if (float(i) >= uSteps) break;
          vec3 p = rd * (t0 + (float(i) + j) * dt);
          float h = p.y;
          // the sharp lower edge, a little ragged; the glow fading upward
          float edge = 1.4 + 0.12 * (n1(p.x * 2.0 + uT * 0.15, 3.0) - 0.5);
          float prof = smoothstep(edge, edge + 0.07, h) * exp(-(h - edge) / 1.7);
          float dsum = 0.0;
          for (int k = 0; k < 4; k++) {
            float I = k == 0 ? uCI.x : k == 1 ? uCI.y : k == 2 ? uCI.z : uCI.w;
            if (I < 0.01) continue;
            vec4 c = uC[k];
            float zc = c.x + c.y * sin(p.x * c.z + c.w + uT * 0.06) + 0.45 * c.y * sin(p.x * c.z * 2.3 - uT * 0.09 + c.w * 1.7);
            float d = p.z - zc;
            float band = exp(-d * d / 0.08);
            // the folds (fine vertical rays) and their shimmer
            float fold = n1(p.x * 5.0 + float(k) * 37.0 + uT * 0.22, 7.0 + float(k));
            fold = pow(fold, 2.2) * 1.7;
            float ray = n1(p.x * 23.0 + float(k) * 11.0 + uT * 0.5, 19.0 + float(k));
            float wave = abs(float(k) - uWave.x) < 0.5 ? uWave.z * exp(-pow((p.x - uWave.y) / 2.2, 2.0)) : 0.0;
            dsum += band * I * (0.2 + fold * (0.55 + 0.55 * ray)) * (1.0 + 1.6 * wave);
          }
          float a = smoothstep(edge + 0.4, 5.5, h);
          vec3 cc = mix(uLow, uHigh, a) + uFringe * exp(-pow((h - edge - 0.04) / 0.07, 2.0)) * 0.7;
          col += cc * dsum * prof * dt;
        }
      }
    }
    // the corona: rays converging toward the zenith (far above the top of the screen), in a fan
    if (uCorona.x > 0.001) {
      float zy = cos(uPitch) / sin(uPitch) / uTanH;
      vec2 v = vec2(s.x, zy - s.y);
      float ang = atan(v.x, v.y), rr = length(v);
      float rays = pow(n1(ang * 160.0 + uCorona.y, 41.0), 3.0) * 1.6 + 0.15;
      // strong at the top of the screen (nearest the zenith), fading toward the horizon
      float shell = smoothstep(zy + 0.35, zy - 1.0, rr);
      col += mix(uLow, uHigh, 0.5) * rays * shell * uCorona.x * 3.2;
    }
    col *= uGain;
    gl_FragColor = vec4(sqrt(clamp(col * 0.5, 0.0, 1.0)), 1.0);
  }
`;
const compositeFS = `
  precision highp float;
  ${NOISE}
  uniform sampler2D uAur;
  uniform float uAspect, uT, uH, uRefl, uGlow, uGain;
  uniform vec3 uGlowCol;
  uniform vec2 uRes;
  uniform vec2 uSafeCenter, uSafeHalfSize;
  uniform float uSafeFeather;
  varying vec2 vUv;
  // the aurora, upscaled with a small tent: the per-pixel step jitter must not show as a pattern
  vec3 aurora(vec2 uv) {
    vec2 o = 1.0 / uRes;
    vec3 a = texture2D(uAur, uv).rgb * 0.36;
    a += (texture2D(uAur, uv + vec2(o.x, o.y) * 1.5).rgb + texture2D(uAur, uv + vec2(-o.x, o.y) * 1.5).rgb + texture2D(uAur, uv + vec2(o.x, -o.y) * 1.5).rgb + texture2D(uAur, uv - o * 1.5).rgb) * 0.16;
    return a * a * 2.0;
  }
  float fbm1(float x, float row) { float s = 0.0, a = 0.5; for (int i = 0; i < 5; i++) { s += a * n1(x, row + float(i) * 13.0); x *= 2.03; a *= 0.5; } return s / 0.97; }
  vec3 starsAt(vec2 s, float twinkle) {
    vec3 c = vec3(0.0);
    for (int l = 0; l < 2; l++) {
      float sc = l == 0 ? 260.0 : 95.0;
      vec2 g = s * sc, cell = floor(g), f = fract(g) - 0.5;
      float h = hash12(cell + float(l) * 31.0), h2 = hash12(cell + 7.7 + float(l));
      float thr = l == 0 ? 0.975 : 0.992;
      if (h > thr) {
        vec2 o = (vec2(h2, fract(h2 * 9.1)) - 0.5) * 0.5;
        float size = (l == 0 ? 0.18 : 0.30) * (0.6 + 0.8 * (h - thr) / (1.0 - thr));
        float b = smoothstep(size, 0.0, length(f - o)) * ((h - thr) / (1.0 - thr) * 0.8 + 0.2);
        vec3 tint = h2 < 0.33 ? vec3(0.75, 0.82, 1.0) : h2 < 0.85 ? vec3(1.0, 0.98, 0.95) : vec3(1.0, 0.82, 0.6);
        c += tint * b * (l == 0 ? 0.55 : 1.1) * (1.0 - twinkle * 0.3 * (0.5 + 0.5 * sin(uT * (2.0 + 5.0 * h2) + h * 60.0)));
      }
    }
    // the Milky Way: a faint diagonal band of dust
    float d = dot(s, normalize(vec2(0.55, 1.0))) - 0.15;
    float band = exp(-d * d / 0.09);
    c += vec3(0.55, 0.6, 0.75) * band * (0.012 + 0.02 * n2(s * 40.0)) * smoothstep(0.3, 0.9, n2(s * 9.0 + 3.0) + 0.3);
    return c;
  }
  // the mountains: heights per layer (far to near)
  float ridge(int i, float x) {
    float base = i == 0 ? 0.13 : i == 1 ? 0.07 : 0.025;
    float amp = i == 0 ? 0.30 : i == 1 ? 0.16 : 0.06;
    float fr = i == 0 ? 1.1 : i == 1 ? 2.0 : 4.5;
    float r = fbm1(x * fr + float(i) * 51.0, 60.0 + float(i) * 40.0);
    return uH + base + amp * max(r - 0.3, 0.0) * 1.8;
  }
  // a tree row on the shore: the nearest fir's signed distance (negative inside) and its snow
  vec2 trees(vec2 s) {
    float best = 1e3, snow = 0.0;
    float cw = 0.028;
    float cell = floor(s.x / cw);
    for (int k = -1; k <= 1; k++) {
      float c = cell + float(k);
      float hh = hash12(vec2(c, 3.0));
      if (hh < 0.18) continue;                              // gaps in the row
      float cx = (c + 0.5 + (hash12(vec2(c, 9.0)) - 0.5) * 0.6) * cw;
      float th = 0.045 + 0.085 * hash12(vec2(c, 5.0)) * (0.6 + 0.4 * sin(c * 0.37) * sin(c * 0.37));
      float y = s.y - (uH + 0.004);
      if (y < -0.01 || y > th + 0.01) continue;
      float tier = fract(y / th * 3.4);
      float w = 0.33 * th * (1.0 - y / th) * (0.72 + 0.4 * (1.0 - tier));
      float d = abs(s.x - cx) - max(w, 0.0015);
      d = max(d, -y);
      d = max(d, y - th);
      if (d < best) { best = d; snow = smoothstep(0.8, 1.0, 1.0 - tier) * smoothstep(-0.006, 0.0, d); }
    }
    return vec2(best, snow);
  }
  // the scene above the shore line, for a screen point (also used, mirrored, by the lake)
  vec3 above(vec2 s, vec2 uv, float px, bool mirror) {
    vec3 sky = starsAt(s, mirror ? 0.0 : 1.0) + aurora(uv);
    sky += vec3(0.010, 0.016, 0.03) * exp(-(s.y - uH) * 3.0);          // the airglow near the horizon
    vec3 col = sky;
    for (int i = 0; i < 3; i++) {
      float rh = ridge(i, s.x);
      if (s.y < rh) {
        float haze = i == 0 ? 0.35 : i == 1 ? 0.18 : 0.0;
        vec3 rock = vec3(0.006, 0.008, 0.014);
        // snow on the high ridges only, in streaks down the gullies
        float hgt = (rh - uH) / 0.4;
        float snowy = smoothstep(rh - 0.02 - 0.05 * hgt, rh - 0.004, s.y) * smoothstep(0.4, 0.62, n2(vec2(s.x * 55.0, s.y * 38.0) + float(i) * 9.0)) * smoothstep(0.08, 0.3, hgt);
        vec3 snow = vec3(0.035, 0.042, 0.06) + uGlowCol * uGlow * 0.16;
        vec3 m = mix(rock, snow, snowy);
        m = mix(m, vec3(0.018, 0.024, 0.04) + uGlowCol * uGlow * 0.03, haze);
        float aa = smoothstep(0.0, px * 1.2, rh - s.y);
        col = mix(col, m, aa);
      }
    }
    vec2 t = trees(s);
    float inside = 1.0 - smoothstep(-px * 0.7, px * 0.7, t.x);
    if (inside > 0.0) {
      // R13: a black body, only the top of each tier catches the aurora's light
      vec3 body = vec3(0.004, 0.006, 0.008);
      vec3 lit = vec3(0.05, 0.055, 0.07) + uGlowCol * uGlow * 0.25;
      col = mix(col, mix(body, lit, t.y * 0.8), inside);
    }
    return col;
  }
  void main() {
    vec2 s = (vUv * 2.0 - 1.0) * vec2(uAspect, 1.0);
    float px = 2.0 / uRes.y;
    vec3 col;
    if (s.y >= uH) col = above(s, vUv, px, false);
    else {
      // the lake: the mirror image above the shore line, darkened and softened, then the ice
      vec2 m = vec2(s.x, 2.0 * uH - s.y);
      vec2 muv = vec2(vUv.x, (m.y / 1.0 + 1.0) * 0.5);
      vec3 r;
      if (uRefl > 0.5) {
        r = above(m, muv, px, true) * 0.5;
        // a softer reflection: two more taps of the aurora, a little further
        r += (aurora(muv + vec2(0.0, 0.006)) + aurora(muv - vec2(0.0, 0.006))) * 0.12;
        r *= 0.62;
      } else {
        // tier 0: no animated reflection, a still glow
        r = uGlowCol * uGlow * 0.08 * exp(-(uH - s.y) * 3.0) + above(m, muv, px, true) * 0.0;
      }
      // the ice: perspective coordinates on the lake plane, Voronoi cracks a pixel wide
      float z = 0.35 / max(uH - s.y, 1e-3);
      vec2 q = vec2(s.x * z, z) * 7.0;
      vec2 cell = floor(q), f = fract(q);
      float d1 = 9.0, d2 = 9.0;
      for (int y = -1; y <= 1; y++) for (int x = -1; x <= 1; x++) {
        vec2 o = vec2(float(x), float(y));
        vec2 pnt = o + vec2(hash12(cell + o), hash12(cell + o + 13.7));
        float d = length(f - pnt);
        if (d < d1) { d2 = d1; d1 = d; } else if (d < d2) d2 = d;
      }
      float e = d2 - d1;
      float crack = (1.0 - smoothstep(0.0, fwidth(e) * 1.2, e)) * exp(-z * 0.6) * step(0.6, hash12(floor(q * 0.5) + floor(f * 2.0))) * 0.7;
      vec3 ice = vec3(0.006, 0.009, 0.016);
      col = ice + r + vec3(0.05, 0.06, 0.075) * crack * (0.4 + uGlow * 0.6);
    }
    // the cover's mask follows the cover (R19), half-height units
    vec2 qq = abs(s - uSafeCenter) - uSafeHalfSize;
    float dz = length(max(qq, 0.0)) + min(max(qq.x, qq.y), 0.0);
    col *= mix(0.45, 1.0, smoothstep(0.0, uSafeFeather, dz));
    gl_FragColor = vec4(col * uGain, 1.0);
  }
`;

export class BorealisVisualizer extends Visualizer {
  constructor(options) {
    super();
    const o = options || {};
    this._tier = o.qualityTier != null && TIERS[o.qualityTier] ? o.qualityTier : 1;
    this._T = TIERS[this._tier];
    const st = parseInt(urlParam('aurorasteps'), 10);
    this._steps = Number.isFinite(st) ? Math.max(4, Math.min(32, st)) : this._T.steps;
    this._forceCorona = urlParam('auroracorona') === '1';
    const fp = parseInt(urlParam('aurorapalette'), 10);
    this._forcePal = Number.isFinite(fp) ? Math.max(0, Math.min(3, fp)) : null;
    this._adapt = new Adapt();
    this._post = new VdjPost({ bloomStrength: 0.22, bloomThreshold: 0.8, grain: 0.012, vignette: 0.3, toneMapping: THREE.NoToneMapping, toneMappingExposure: 1 });
    if (urlParam('auroraprobe') === '1' && typeof window !== 'undefined') window.__borealis = this;
  }

  get postProcessing() { return this._post.value; }

  init(scene) {
    this._scene = scene;
    this._ortho = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
    const nz = new Uint8Array(256 * 256 * 4); let sd = 97; const rnd = () => { sd = (Math.imul(sd, 1664525) + 1013904223) >>> 0; return sd / 4294967296; };
    for (let i = 0; i < nz.length; i++) nz[i] = Math.floor(rnd() * 256);
    this._noise = new THREE.DataTexture(nz, 256, 256, THREE.RGBAFormat, THREE.UnsignedByteType);
    this._noise.minFilter = this._noise.magFilter = THREE.LinearFilter; this._noise.wrapS = this._noise.wrapT = THREE.RepeatWrapping; this._noise.needsUpdate = true;
    const tanH = Math.tan(FOV_DEG / 2 * Math.PI / 180);
    const pitch = Math.atan(-HORIZON * tanH);                // the camera looks up so the sky's horizon meets the shore
    this._AU = {
      uNoise: { value: this._noise }, uT: { value: 0 }, uSteps: { value: this._steps }, uAspect: { value: 1.78 }, uTanH: { value: tanH }, uPitch: { value: pitch }, uGain: { value: 1 },
      uC: { value: CURTAINS.map((c) => new THREE.Vector4(c[0], c[1], c[2], c[3])) }, uCI: { value: new THREE.Vector4(1, 1, 1, 1) },
      uWave: { value: new THREE.Vector4(0, -99, 0, 0) }, uCorona: { value: new THREE.Vector2(0, 0) },
      uLow: { value: new THREE.Color() }, uHigh: { value: new THREE.Color() }, uFringe: { value: new THREE.Color() }
    };
    this._auScene = new THREE.Scene();
    const aq = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.ShaderMaterial({ uniforms: this._AU, vertexShader: quadVS, fragmentShader: auroraFS, depthTest: false, depthWrite: false }));
    aq.frustumCulled = false; this._auScene.add(aq);
    this._auTarget = null;
    this._CU = {
      uNoise: { value: this._noise }, uAur: { value: null }, uAspect: safeZoneUniforms.uAspect, uT: { value: 0 }, uH: { value: HORIZON }, uRefl: { value: this._T.refl ? 1 : 0 },
      uGlow: { value: 0.5 }, uGain: { value: 1 }, uGlowCol: { value: new THREE.Color() }, uRes: { value: new THREE.Vector2(1600, 900) },
      uSafeCenter: safeZoneUniforms.uSafeCenter, uSafeHalfSize: safeZoneUniforms.uSafeHalfSize, uSafeFeather: safeZoneUniforms.uSafeFeather
    };
    this._display = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.ShaderMaterial({ uniforms: this._CU, vertexShader: quadVS, fragmentShader: compositeFS, depthTest: false, depthWrite: false, extensions: { derivatives: true } }));
    this._display.frustumCulled = false; this._display.renderOrder = -1000;
    const self = this;
    this._display.onBeforeRender = function (renderer) { self._render(renderer); };
    scene.add(this._display);

    this._seed = 21; this._trackSeen = undefined;
    this._time = 0; this._tt = 0; this._freePhase = 0; this._beats = 0;
    this._pal = this._forcePal != null ? this._forcePal : 0;
    const p = PALETTES[this._pal];
    this._low = new THREE.Color(...p.low); this._high = new THREE.Color(...p.high); this._fringe = new THREE.Color(...p.fringe);
    this._waveK = 0; this._waveAt = -99; this._barEnv = 0; this._coronaAt = -99; this._coronaSeed = 0; this._coronaScene = -1;
    this._intensity = 0.35;
    this._clock = new SceneClock({ period: SCENE_S, start: 'random' });
    this._log = [];
  }

  _rnd() { this._seed = (Math.imul(this._seed, 1664525) + 1013904223) >>> 0; return this._seed / 4294967296; }
  _note(e) { e.t = +this._time.toFixed(2); this._log.push(e); if (this._log.length > 30) this._log.shift(); }
  _corona(why) { this._coronaAt = this._time; this._coronaSeed = this._rnd() * 100; this._coronaScene = Math.floor(this._clock.elapsed / SCENE_S); this._note({ what: 'corona', why }); }

  update(deltaTime, profile) {
    const dt = Math.min(Math.max(deltaTime, 0), 0.1);
    const playing = profile.playbackState === 'PLAYING';
    const a = this._adapt.update(playing ? dt : 0, profile);
    if (profile.trackId !== this._trackSeen) {
      this._trackSeen = profile.trackId;
      this._seed = hashStr(String(profile.title || '') + '|' + String(profile.artist || '')) || 21;
    }
    if (!playing) return;                                       // frozen, never black
    this._time += dt;
    this._clock.update(dt);
    this._intensity += (a.intensity - this._intensity) * Math.min(1, dt / 3);
    const I = this._intensity, t = this._time;
    const bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || NO_BPM;
    const beatS = 60 / bpm;
    const pulsed = a.pulse > 0.2;
    let beat = a.beat;
    if (!pulsed) { this._freePhase += dt / (beatS * 2); if (this._freePhase >= 1) { this._freePhase -= 1; beat = true; } }
    if (beat) this._beats++;
    const bar = a.bar || (!pulsed && beat && this._beats % 4 === 0);
    const phrase = a.phrase || (!pulsed && beat && this._beats % 16 === 0);
    if (bar) this._lastBarT = t;
    // calm: slow and pale; the sky's own time runs with the intensity
    this._tt += dt * (0.45 + 0.9 * I);
    const U = this._AU;
    // the beat: a wave along one curtain, a smooth envelope over a beat and a half
    if (beat) { this._waveK = (this._waveK + 1) % this._T.curtains; this._waveAt = t; }
    const wp = (t - this._waveAt) / (1.5 * beatS);
    U.uWave.value.set(this._waveK, -14 + 28 * wp, wp < 1 ? Math.sin(Math.PI * wp) * (0.35 + 0.65 * I) : 0, 0);
    // the bar: the curtains ripple harder, then rest
    if (bar) this._barEnv = 1;
    this._barEnv *= Math.pow(0.5, dt / 1.5);
    for (let k = 0; k < 4; k++) U.uC.value[k].y = CURTAINS[k][1] * (1 + 0.45 * this._barEnv);
    // the phrase: the palette, cross-faded over 3 s
    if (phrase && this._forcePal == null) {
      let r = this._rnd(), k = 0; while (k < 3 && r > PAL_WEIGHTS[k]) { r -= PAL_WEIGHTS[k]; k++; }
      if (k === this._pal) k = (k + 1) % 3;
      this._pal = k; this._note({ what: 'palette', k });
    }
    const P = PALETTES[this._pal], f = Math.min(1, dt / 3);
    this._low.lerp(_c.setRGB(...P.low), f); this._high.lerp(_c.setRGB(...P.high), f); this._fringe.lerp(_c.setRGB(...P.fringe), f);
    U.uLow.value.copy(this._low); U.uHigh.value.copy(this._high); U.uFringe.value.copy(this._fringe);
    // the corona: on an accent, or around the middle of the scene (one per scene at least)
    const sceneIdx = Math.floor(this._clock.elapsed / SCENE_S);
    if (a.onset > 0.85 && I > 0.45 && t - this._coronaAt > 12) this._corona('accent');
    if (this._coronaScene !== sceneIdx && this._clock.t > SCENE_S * 0.45 && this._clock.t < SCENE_S * 0.6) this._corona('scene');
    if (this._forceCorona && t - this._coronaAt > CORONA_S + 2) this._corona('debug');
    const ca = t - this._coronaAt;
    const cEnv = ca < CORONA_S ? Math.sin(Math.PI * Math.min(1, ca / CORONA_S)) * (ca < 0.6 ? ca / 0.6 : 1) : 0;
    U.uCorona.value.set(cEnv * (0.6 + 0.6 * I), this._coronaSeed);
    // intensities: pale when calm; one curtain per tier fewer
    for (let k = 0; k < 4; k++) U.uCI.value.setComponent(k, k < this._T.curtains ? (0.55 + 0.55 * I) * (k === 0 ? 1 : 0.85) : 0);
    U.uT.value = this._tt;
    U.uGain.value = 0.16 * (0.6 + 0.5 * I) * (1 + 0.8 * cEnv);
    // the glow on the snow and the ice
    this._CU.uGlowCol.value.copy(this._low).lerp(this._high, 0.25);
    this._CU.uGlow.value = (0.35 + 0.5 * I) * (1 + 0.6 * cEnv);
    this._CU.uT.value = t;
    this._CU.uGain.value = Math.min(1, a.lumaCap / 0.30);
    this._post.update(vdjBarChroma(t - (this._lastBarT || -9)));
  }

  _render(renderer) {
    const cur = renderer.getRenderTarget();
    let w, h;
    if (cur) { w = cur.width; h = cur.height; } else { const b = renderer.getDrawingBufferSize(_size); w = b.x; h = b.y; }
    const hw = Math.max(16, Math.round(w / 2)), hh = Math.max(16, Math.round(h / 2));
    if (!this._auTarget || this._auTarget.width !== hw || this._auTarget.height !== hh) {
      if (this._auTarget) this._auTarget.dispose();
      this._auTarget = new THREE.WebGLRenderTarget(hw, hh, { type: THREE.UnsignedByteType, depthBuffer: false, minFilter: THREE.LinearFilter, magFilter: THREE.LinearFilter });
    }
    this._AU.uAspect.value = w / h;
    this._CU.uRes.value.set(w, h);
    const xr = renderer.xr.enabled; renderer.xr.enabled = false;
    renderer.setRenderTarget(this._auTarget); renderer.render(this._auScene, this._ortho);
    renderer.setRenderTarget(cur);
    renderer.xr.enabled = xr;
    this._CU.uAur.value = this._auTarget.texture;
  }

  dispose() {
    if (this._scene && this._display) { this._display.onBeforeRender = null; this._scene.remove(this._display); }
    if (this._display) { this._display.geometry.dispose(); this._display.material.dispose(); }
    if (this._auTarget) this._auTarget.dispose();
    if (this._noise) this._noise.dispose();
    if (this._adapt) this._adapt.dispose();
    if (typeof window !== 'undefined' && window.__borealis === this) window.__borealis = null;
    this._scene = null;
  }
}

const _size = new THREE.Vector2();
const _c = new THREE.Color();
